import React, {useCallback, useEffect, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useNavigate} from 'react-router-dom';
import {FiRefreshCw, FiShoppingBag} from 'react-icons/fi';

import {getRestaurants} from '../api/api';
import OutletCard from '../components/OutletCard';

const logo = '/logo192.png';

export default function Browse() {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await getRestaurants({isAvailable: true});
      const fetched = response?.data || response || [];
      setRestaurants(
        Array.isArray(fetched)
          ? fetched.filter(item => Boolean(item?.isOpen && item?.isAvailable))
          : [],
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load outlets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRestaurants();
    const interval = window.setInterval(fetchRestaurants, 15000);
    return () => window.clearInterval(interval);
  }, [fetchRestaurants]);

  const openRestaurant = restaurant => {
    navigate(`/restaurant/${restaurant.id}`, {
      state: {outlet: restaurant, selectedTab: 'Delivery'},
    });
  };

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <section className={css(styles.hero)}>
          <div>
            <p className={css(styles.kicker)}>Browse YaBatho</p>
            <h1 className={css(styles.title)}>Available outlets</h1>
            <p className={css(styles.subtitle)}>
              Discover local restaurants that are open and ready for your order.
            </p>
          </div>
          <div className={css(styles.heroBadge)}>
            <img src={logo} alt="YaBatho" className={css(styles.logo)} />
          </div>
        </section>

        <section className={css(styles.contentCard)}>
          <header className={css(styles.sectionHeader)}>
            <div>
              <p className={css(styles.sectionKicker)}>Open now</p>
              <h2 className={css(styles.sectionTitle)}>Browse restaurants</h2>
              <p className={css(styles.sectionCopy)}>Every outlet shown below is currently available.</p>
            </div>
            <div className={css(styles.headerActions)}>
              <span className={css(styles.countBadge)}>
                {restaurants.length} outlet{restaurants.length === 1 ? '' : 's'}
              </span>
              <button type="button" className={css(styles.refreshButton)} onClick={fetchRestaurants} disabled={loading}>
                <FiRefreshCw /> Refresh
              </button>
            </div>
          </header>

          {loading ? (
            <div className={css(styles.stateCard)}>
              <div className={css(styles.spinner)} />
              <h3>Loading open outlets</h3>
              <p>Please wait while we refresh restaurant availability.</p>
            </div>
          ) : errorMessage ? (
            <div className={css(styles.stateCard)}>
              <div className={css(styles.errorBadge)}>!</div>
              <h3>Could not load outlets</h3>
              <p>{errorMessage}</p>
              <button type="button" className={css(styles.retryButton)} onClick={fetchRestaurants}>Try again</button>
            </div>
          ) : restaurants.length ? (
            <div className={css(styles.grid)}>
              {restaurants.map(restaurant => (
                <OutletCard
                  key={restaurant.id || restaurant._id}
                  outlet={restaurant}
                  selectedTab="Delivery"
                  badge={restaurant?.topRated ? 'Top rated' : null}
                  onClick={() => openRestaurant(restaurant)}
                />
              ))}
            </div>
          ) : (
            <div className={css(styles.stateCard)}>
              <div className={css(styles.emptyBadge)}><FiShoppingBag /></div>
              <h3>No open outlets right now</h3>
              <p>Please check again soon—availability refreshes automatically.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const styles = StyleSheet.create({
  page: {minHeight: '100vh', width: '100%', backgroundColor: '#FFFDF7', color: '#0F3D2E', fontFamily: 'Inter, Segoe UI, Arial, sans-serif', padding: '24px 24px 112px', boxSizing: 'border-box', '@media (max-width: 720px)': {padding: '14px 12px 104px'}},
  shell: {width: '100%', maxWidth: '1180px', margin: '0 auto'},
  hero: {backgroundColor: '#0F3D2E', border: '1px solid rgba(201,161,74,.45)', borderRadius: '32px', padding: '30px', boxShadow: '0 22px 50px rgba(15,61,46,.16)', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: '24px', marginBottom: '22px', '@media (max-width: 600px)': {padding: '22px', borderRadius: '26px'}},
  kicker: {margin: '0 0 8px', color: '#C9A14A', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px'},
  title: {margin: 0, fontSize: 'clamp(32px, 5vw, 44px)', lineHeight: 1.05, letterSpacing: '-1.6px'},
  subtitle: {maxWidth: '650px', margin: '14px 0 0', color: '#F4EBD8', fontSize: '15px', lineHeight: 1.7},
  heroBadge: {width: '76px', height: '76px', flex: '0 0 76px', borderRadius: '50%', border: '2px solid #C9A14A', display: 'grid', placeItems: 'center', overflow: 'hidden', '@media (max-width: 520px)': {width: '58px', height: '58px', flexBasis: '58px'}},
  logo: {width: '82%', height: '82%', objectFit: 'contain'},
  contentCard: {backgroundColor: '#fff', border: '1px solid #E6D8BE', borderRadius: '30px', padding: '22px', boxShadow: '0 12px 34px rgba(30,30,30,.06)', '@media (max-width: 600px)': {padding: '16px', borderRadius: '24px'}},
  sectionHeader: {display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '18px', marginBottom: '22px', '@media (max-width: 650px)': {alignItems: 'flex-start', flexDirection: 'column'}},
  sectionKicker: {margin: '0 0 5px', color: '#C9A14A', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '.9px'},
  sectionTitle: {margin: 0, fontSize: '26px', fontWeight: '900', letterSpacing: '-.7px'},
  sectionCopy: {margin: '7px 0 0', color: '#8C8170', fontSize: '13px'},
  headerActions: {display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'},
  countBadge: {padding: '10px 14px', borderRadius: '999px', color: '#0F3D2E', backgroundColor: '#F4EBD8', border: '1px solid #E6D8BE', fontSize: '12px', fontWeight: '900'},
  refreshButton: {minHeight: '40px', padding: '9px 13px', borderRadius: '14px', border: '1px solid #0F3D2E', color: '#0F3D2E', backgroundColor: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: '900'},
  grid: {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '20px'},
  stateCard: {minHeight: '250px', borderRadius: '24px', border: '1px solid #E6D8BE', backgroundColor: '#FFFDF7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '28px', h3: {margin: '12px 0 7px', fontSize: '20px'}, p: {maxWidth: '420px', margin: 0, color: '#8C8170', lineHeight: 1.6}},
  spinner: {width: '36px', height: '36px', borderRadius: '50%', border: '4px solid #F4EBD8', borderTopColor: '#C9A14A'},
  errorBadge: {width: '52px', height: '52px', borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#fff', backgroundColor: '#D9534F', fontSize: '22px', fontWeight: '900'},
  emptyBadge: {width: '58px', height: '58px', borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#C9A14A', backgroundColor: '#0F3D2E', fontSize: '24px'},
  retryButton: {marginTop: '16px', minHeight: '44px', padding: '10px 17px', border: 0, borderRadius: '15px', color: '#0F3D2E', backgroundColor: '#C9A14A', fontWeight: '900', cursor: 'pointer'},
});
